/**
 * Inline record/edit payment form — Saved badge + toast (LienTab #337 pattern).
 */
import { useRef, useState, type FormEvent } from 'react'
import SavedBadge from '../../../components/SavedBadge'
import { parseMoneyInput } from '../../../lib/formatters'
import MoneyInput from '../../../components/MoneyInput'
import { formatMoneyBlurValue } from '../../../lib/moneyInputCore'
import { useActionToast } from '../../../lib/useActionToast'

export interface PaymentFormState {
  amount: string
  method: string
  checkNumber: string
  notes: string
}

export const EMPTY_PAYMENT_FORM: PaymentFormState = {
  amount: '',
  method: 'Check',
  checkNumber: '',
  notes: '',
}

function fmtMoney(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

interface Props {
  appNumber: number
  canEdit: boolean
  initial: PaymentFormState
  /** True when opening an already-paid pay app for edit. */
  alreadyPaid: boolean
  onCancel: () => void
  onSubmit: (payload: {
    amount: number
    payment_method: string
    check_number?: string
    notes?: string
  }) => Promise<void>
}

export default function PaymentRecordInline({
  appNumber,
  canEdit,
  initial,
  alreadyPaid,
  onCancel,
  onSubmit,
}: Props) {
  const { showToast, Toast } = useActionToast()
  const saveInFlight = useRef(false)
  const [form, setForm] = useState<PaymentFormState>(initial)
  const [baseline, setBaseline] = useState<PaymentFormState>(
    alreadyPaid ? initial : EMPTY_PAYMENT_FORM,
  )
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [confirmed, setConfirmed] = useState(alreadyPaid)

  const dirty =
    form.amount !== baseline.amount
    || form.method !== baseline.method
    || form.checkNumber !== baseline.checkNumber
    || form.notes !== baseline.notes

  const showSaved = confirmed && !editing && !dirty

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    e.stopPropagation()
    const amount = parseMoneyInput(form.amount)
    if (!amount || busy || saveInFlight.current || !dirty) return
    saveInFlight.current = true
    setBusy(true)
    setErr(null)
    try {
      await onSubmit({
        amount,
        payment_method: form.method,
        check_number: form.method === 'Check' ? form.checkNumber || undefined : undefined,
        notes: form.notes.trim() || undefined,
      })
      const saved = { ...form, amount: formatMoneyBlurValue(form.amount) }
      setForm(saved)
      setBaseline(saved)
      setConfirmed(true)
      setEditing(false)
      showToast('Payment recorded')
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Failed to record payment')
    } finally {
      setBusy(false)
      saveInFlight.current = false
    }
  }

  if (showSaved) {
    return (
      <div
        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
        data-testid="payment-record-saved"
      >
        {Toast}
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
          {fmtMoney(parseMoneyInput(baseline.amount) || 0)} · {baseline.method}
        </span>
        <SavedBadge testId="payment-record-saved-badge" />
        <button
          type="button"
          className="btn b-ol btn-sm"
          data-testid="payment-record-edit"
          onClick={() => setEditing(true)}
        >
          Edit
        </button>
        <button type="button" className="btn b-dk btn-sm" onClick={onCancel}>
          Done
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} data-testid="payment-record-form">
      {Toast}
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 10 }}>
        {canEdit ? 'Edit Payment' : 'Record Payment'} · PA #{appNumber}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <label>
          <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 }}>Amount</span>
          <MoneyInput
            className="fld-i mono"
            value={form.amount}
            onChange={v => {
              setForm(f => ({ ...f, amount: v }))
              setEditing(true)
            }}
            required
          />
        </label>
        <label>
          <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 }}>Payment method</span>
          <select
            className="fld-i"
            value={form.method}
            onChange={e => {
              setForm(f => ({ ...f, method: e.target.value }))
              setEditing(true)
            }}
          >
            <option value="Check">Check</option>
            <option value="ACH">ACH</option>
            <option value="Wire">Wire Transfer</option>
            <option value="Cash">Cash</option>
            <option value="Other">Other</option>
          </select>
        </label>
      </div>
      {form.method === 'Check' && (
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 }}>Check # (optional)</span>
          <input
            className="fld-i"
            value={form.checkNumber}
            onChange={e => {
              setForm(f => ({ ...f, checkNumber: e.target.value }))
              setEditing(true)
            }}
            placeholder="e.g. 12345"
          />
        </label>
      )}
      <label style={{ display: 'block', marginBottom: 12 }}>
        <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 }}>Notes (optional)</span>
        <input
          className="fld-i"
          value={form.notes}
          onChange={e => {
            setForm(f => ({ ...f, notes: e.target.value }))
            setEditing(true)
          }}
          placeholder="e.g. Received 4/6/2026"
        />
      </label>
      {err && <p style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn b-ol btn-sm" onClick={onCancel}>Cancel</button>
        <button
          type="submit"
          className="btn b-dk btn-sm"
          data-testid="payment-record-save"
          disabled={busy || !form.amount || !dirty}
        >
          {busy ? 'Recording…' : 'Record Payment'}
        </button>
      </div>
    </form>
  )
}
